import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Building2, FileSignature, Loader2, Lock, Smartphone, Stethoscope, Upload, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from './Button';
import {
  documentBestandUrl,
  downloadAuditCertificaat,
  getDocumentVoorWerkgever,
  tekenDocumentAlsWerkgever,
  uploadExternDocument,
  type PoortwachterCase,
  type PoortwachterDocumentMeta,
} from '../services/lopendeTrajectenApi';

/**
 * Digital Signing Wrapper — werkgever-kant.
 *
 *   <VerzegelingsBadges>   Kolom 7: 🧑‍⚕️ Arts ➔ 🏢 Werkgever ➔ 🔒 Medewerker, met
 *                          downloadknop naar het Audit-Certificaat zodra 🟢
 *   <SigningWrapperModal>  split-screen: links het document (tekst of PDF),
 *                          rechts de akkoordverklaring + [Teken als werkgever]
 *   <UploadExternModal>    externe PDF van de arbo-arts uploaden (tekent direct)
 */

export const QK_POORTWACHTER = ['lopende-trajecten', 'poortwachter'];
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;
const fmtDateTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : null);
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

export const UPLOAD_SOORTEN: Array<{ key: string; label: string }> = [
  { key: 'probleemanalyse', label: 'Probleemanalyse (week 6)' },
  { key: 'bijstelling', label: 'Bijstelling Plan van Aanpak' },
  { key: 'periodieke_evaluatie', label: 'Periodieke evaluatie' },
  { key: 'eerstejaarsevaluatie', label: 'Eerstejaarsevaluatie (week 52)' },
];

// ─── Kolom 7: badges ──────────────────────────────────────────────────────────

const Badge: React.FC<{ tone: 'green' | 'orange' | 'gray' | 'blue'; children: React.ReactNode; title?: string }> = ({ tone, children, title }) => {
  const tones = { green: 'bg-green-100 text-green-800', orange: 'bg-orange-100 text-orange-800', gray: 'bg-gray-100 text-gray-600', blue: 'bg-blue-100 text-blue-800' };
  return <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${tones[tone]}`}>{children}</span>;
};

export const VerzegelingsBadges: React.FC<{ c: PoortwachterCase; doc: PoortwachterDocumentMeta; onTeken: (doc: PoortwachterDocumentMeta) => void }> = ({ c, doc, onTeken }) => {
  const { t } = useTranslation('employer');
  const [bezig, setBezig] = useState(false);
  const werkgever = !!doc.werkgever_ondertekend_op;
  const medewerker = doc.status === 'ondertekend';
  const download = async () => {
    setBezig(true);
    try {
      await downloadAuditCertificaat(c.id, doc, c.case_code);
    } catch (e: any) {
      toast.error(apiMessage(e, t('poortwachterSigning.foutCertificaat')));
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="text-xs">
      <div className="font-medium text-gray-800 truncate max-w-[220px]" title={doc.titel}>{doc.soort_label}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        {doc.bron !== 'werkgever_formulier' && (
          <Badge tone="blue" title={doc.bron === 'werkgever_upload' ? t('poortwachterSigning.pdfGeupload', { naam: doc.bestand_naam ?? '' }) : t('poortwachterSigning.opgesteldDoor', { naam: doc.professional_naam })}>
            <Stethoscope className="w-3 h-3" /> {doc.bron === 'werkgever_upload' ? t('poortwachterSigning.artsGeupload') : t('poortwachterSigning.artsVrijgegeven')}
          </Badge>
        )}
        <span className="text-gray-400">➔</span>
        {werkgever ? (
          <Badge tone="green" title={t('poortwachterSigning.getekendTitle', { datum: fmtDateTime(doc.werkgever_ondertekend_op) })}><Building2 className="w-3 h-3" /> {t('poortwachterSigning.werkgeverGetekend')}</Badge>
        ) : (
          <button type="button" onClick={() => onTeken(doc)} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-orange-500 text-white hover:bg-orange-600">
            <FileSignature className="w-3 h-3" /> {t('poortwachterSigning.werkgeverTeken')}
          </button>
        )}
        <span className="text-gray-400">➔</span>
        {medewerker ? (
          <Badge tone="green" title={t('poortwachterSigning.verzegeldTitle', { datum: fmtDateTime(doc.ondertekend_op), zegel: doc.zegel_hash?.slice(0, 12) })}><Lock className="w-3 h-3" /> {t('poortwachterSigning.medewerkerVerzegeld')}</Badge>
        ) : (
          <Badge tone={werkgever ? 'orange' : 'gray'} title={doc.sms_verzonden_op ? t('poortwachterSigning.smsVerstuurd', { datum: fmtDateTime(doc.sms_verzonden_op) }) : t('poortwachterSigning.geenSms')}>
            <Smartphone className="w-3 h-3" /> {werkgever ? t('poortwachterSigning.medewerkerWachtSms') : t('poortwachterSigning.medewerkerWachtWerkgever')}
          </Badge>
        )}
        {medewerker && (
          <button type="button" onClick={download} disabled={bezig} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50" title={t('poortwachterSigning.certificaatTitle')}>
            {bezig ? <Loader2 className="w-3 h-3 animate-spin" /> : <Award className="w-3 h-3" />} {t('poortwachterSigning.certificaat')}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Split-screen ondertekenen ────────────────────────────────────────────────

export const SigningWrapperModal: React.FC<{ c: PoortwachterCase; docId: string; onSluit: () => void }> = ({ c, docId, onSluit }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const [akkoord, setAkkoord] = useState(false);
  const { data: doc, isLoading, error } = useQuery({ queryKey: [...QK_POORTWACHTER, 'document', docId], queryFn: () => getDocumentVoorWerkgever(c.id, docId) });
  const tekenM = useMutation({
    mutationFn: () => tekenDocumentAlsWerkgever(c.id, docId),
    onSuccess: (r) => {
      toast.success(r.sms_verzonden ? t('poortwachterSigning.toastGetekendSms') : t('poortwachterSigning.toastGetekendZonderSms', { reden: r.reden ?? '' }));
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
      onSluit();
    },
    onError: (e: any) => toast.error(apiMessage(e, t('poortwachterSigning.foutTekenen'))),
  });
  const alGetekend = !!doc?.werkgever_ondertekend_op;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label={t('poortwachterSigning.signingAria')}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl my-6">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-orange-600" /> {doc ? t('poortwachterSigning.signingTitelMetSoort', { caseCode: c.case_code, soort: doc.soort_label }) : t('poortwachterSigning.signingTitel', { caseCode: c.case_code })}
          </h3>
          <button type="button" onClick={onSluit} aria-label={t('poortwachterSigning.sluiten')} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {isLoading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : !doc ? (
          <div className="p-6 text-sm text-red-700">{apiMessage(error, t('poortwachterSigning.foutDocument'))}</div>
        ) : (
          <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x">
            {/* Links: het document */}
            <div className="p-5 min-h-[420px]">
              <div className="text-xs text-gray-500 mb-2 flex flex-wrap gap-x-3">
                <span>{doc.titel}</span>
                <span>{t('poortwachterSigning.vrijgegeven', { datum: fmtDateTime(doc.vrijgegeven_op) ?? '—' })}</span>
                <span className="font-mono" title={doc.document_hash ?? ''}>SHA-256 {doc.document_hash?.slice(0, 16)}…</span>
              </div>
              {doc.heeft_bestand ? (
                <iframe title={t('poortwachterSigning.iframeTitle')} src={documentBestandUrl(c.id, doc.id)} className="w-full h-[520px] rounded-lg border border-gray-200 bg-gray-50" />
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed max-h-[520px] overflow-y-auto p-4 rounded-lg border border-gray-200 bg-gray-50">{doc.inhoud}</pre>
              )}
            </div>
            {/* Rechts: de akkoordverklaring */}
            <div className="p-5 space-y-4 bg-orange-50/40">
              <h4 className="font-semibold text-gray-900">{t('poortwachterSigning.akkoordTitel')}</h4>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal ml-4">
                <li><Trans t={t} i18nKey="poortwachterSigning.stap1" components={{ strong: <strong /> }} /></li>
                <li><Trans t={t} i18nKey="poortwachterSigning.stap2" components={{ strong: <strong /> }} /></li>
                <li><Trans t={t} i18nKey="poortwachterSigning.stap3" components={{ strong: <strong /> }} /></li>
              </ol>
              {alGetekend ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
                  {doc.status === 'ondertekend'
                    ? t('poortwachterSigning.getekendEnVerzegeld', { datum: fmtDateTime(doc.werkgever_ondertekend_op), verzegeld: fmtDateTime(doc.ondertekend_op) })
                    : t('poortwachterSigning.getekendWachtOpMedewerker', { datum: fmtDateTime(doc.werkgever_ondertekend_op) })}
                </div>
              ) : (
                <>
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-orange-300 bg-white text-sm text-gray-800 cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} />
                    <span>{doc.akkoordverklaring}</span>
                  </label>
                  <Button className="w-full" disabled={!akkoord || tekenM.isPending} onClick={() => tekenM.mutate()}>
                    {tekenM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSignature className="w-4 h-4 mr-1" /> {t('poortwachterSigning.tekenKnop')}</>}
                  </Button>
                  <p className="text-[11px] text-gray-500">{t('poortwachterSigning.naHandtekening', { hash: doc.document_hash?.slice(0, 8) })}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Externe PDF (arbo-arts) uploaden ─────────────────────────────────────────

export const UploadExternModal: React.FC<{ c: PoortwachterCase; soortDefault?: string; onSluit: () => void }> = ({ c, soortDefault, onSluit }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const [soort, setSoort] = useState(soortDefault ?? 'probleemanalyse');
  const [titel, setTitel] = useState('');
  const [bestand, setBestand] = useState<File | null>(null);
  const [akkoord, setAkkoord] = useState(false);
  const m = useMutation({
    mutationFn: () => uploadExternDocument(c.id, { soort, titel: titel.trim(), bestand: bestand! }),
    onSuccess: (r) => {
      toast.success(r.sms_verzonden ? t('poortwachterSigning.toastUploadSms') : t('poortwachterSigning.toastUploadZonderSms', { reden: r.reden ?? '' }));
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
      onSluit();
    },
    onError: (e: any) => toast.error(apiMessage(e, t('poortwachterSigning.foutUpload'))),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label={t('poortwachterSigning.uploadAria')}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Upload className="w-4 h-4 text-blue-600" /> {t('poortwachterSigning.uploadTitel', { caseCode: c.case_code })}</h3>
          <button type="button" onClick={onSluit} aria-label={t('poortwachterSigning.sluiten')} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelClass} htmlFor="up-soort">{t('poortwachterSigning.documentsoort')}</label>
            <select id="up-soort" className={inputClass} value={soort} onChange={(e) => setSoort(e.target.value)}>
              {UPLOAD_SOORTEN.map((s) => <option key={s.key} value={s.key}>{t(`poortwachterSigning.uploadSoort.${s.key}`)}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="up-titel">{t('poortwachterSigning.titelLabel')}</label>
            <input id="up-titel" className={inputClass} value={titel} maxLength={255} onChange={(e) => setTitel(e.target.value)} placeholder={t('poortwachterSigning.titelPlaceholder')} />
          </div>
          <div>
            <label className={labelClass} htmlFor="up-bestand">{t('poortwachterSigning.bestandLabel')}</label>
            <input id="up-bestand" type="file" accept="application/pdf" className="text-sm" onChange={(e) => setBestand(e.target.files?.[0] ?? null)} />
          </div>
          <label className="flex items-start gap-2 p-3 rounded-lg border border-orange-300 bg-orange-50 text-xs text-gray-800 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} />
            <span>Ik verklaar namens de werkgever dit document te hebben ingezien en akkoord te gaan met de re-integratie-afspraken erin. De SHA-256 van de PDF wordt bevroren; de medewerker tekent daarna via SMS-2FA.</span>
          </label>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onSluit}>{t('poortwachterSigning.annuleren')}</Button>
            <Button size="sm" disabled={!bestand || !titel.trim() || !akkoord || m.isPending} onClick={() => m.mutate()}>
              {m.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSignature className="w-3.5 h-3.5 mr-1" /> {t('poortwachterSigning.uploadTeken')}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
